<script setup>
import { useApi } from '@/utils/api'
import { useAbility } from '@casl/vue'

definePage({
  meta: {
    layout: 'default',
    action: 'read',
    subject: 'Product',
  },
})

const route = useRoute()
const router = useRouter()
const ability = useAbility()
const productId = route.params.id

// State
const loading = ref(true)
const product = ref(null)
const recipeDialog = ref(false)
const selectedVariant = ref(null)
const recipeItems = ref([])
const ingredients = ref([])
const recipeLoading = ref(false)
const recipeSaving = ref(false)
const snackbar = ref({ show: false, text: '', color: 'success' })

// Addendum 10, Modul AO — Tier Grosir (mirror pola Recipe di atas)
const priceTierDialog = ref(false)
const priceTierVariant = ref(null)
const priceTierLoading = ref(false)
const priceTierSaving = ref(false)
const priceTierItems = ref([])

// Fetch product detail
const fetchProduct = async () => {
  loading.value = true
  try {
    const response = await useApi(`/api/v1/products/${productId}`).get()
    const data = response?.data ?? response
    product.value = data?.data ?? data
  } catch (err) {
    showSnackbar('Gagal memuat data produk', 'error')
    console.error(err)
  } finally {
    loading.value = false
  }
}

// Fetch ingredients
const fetchIngredients = async () => {
  try {
    const response = await useApi('/api/v1/ingredients', { 
      query: { per_page: 200 } 
    }).get()
    const data = response?.data ?? response
    ingredients.value = Array.isArray(data) ? data : (data?.data ?? [])
  } catch (err) {
    console.error('Failed to load ingredients:', err)
  }
}

// Open recipe dialog
const openRecipe = async (variant) => {
  selectedVariant.value = variant
  recipeDialog.value = true
  recipeLoading.value = true
  recipeItems.value = []

  try {
    await fetchIngredients()
    
    const response = await useApi(`/api/v1/variants/${variant.id}/recipe`).get()
    const recipeData = response?.data ?? response

    // ✅ FIX: Parsing yang benar - recipeData.recipes (BUKAN recipeData.data.recipes)
    recipeItems.value = (recipeData?.recipes ?? []).map(i => ({
      ingredient_id: i.ingredient?.id,
      ingredient_name: i.ingredient?.name,
      quantity: i.quantity,
      unit_id: i.unit?.id ?? null,  // ✅ SIMPAN unit_id
      unit_name: i.unit?.name ?? '',
    }))
  } catch (err) {
    console.error('Failed to load recipe:', err)
    showSnackbar('Gagal memuat recipe', 'error')
  } finally {
    recipeLoading.value = false
  }
}

// Save recipe
const saveRecipe = async () => {
  recipeSaving.value = true
  try {
    const items = recipeItems.value
      .filter(i => i.ingredient_id && i.unit_id)  // ✅ Filter unit_id juga
      .map(i => ({
        ingredient_id: i.ingredient_id,
        unit_id: i.unit_id,  // ✅ KIRIM unit_id
        quantity: Number(i.quantity)
      }))

    await useApi(`/api/v1/variants/${selectedVariant.value.id}/recipe`).put({ 
      body: { items } 
    })
    
    showSnackbar('Recipe berhasil disimpan')
    recipeDialog.value = false
    
    // Reload product
    await fetchProduct()
  } catch (err) {
    console.error('Save recipe error:', err)
    showSnackbar(err?.data?.message ?? 'Gagal menyimpan recipe', 'error')
  } finally {
    recipeSaving.value = false
  }
}

// Add recipe item
const addRecipeItem = () => {
  recipeItems.value.push({ 
    ingredient_id: null, 
    quantity: 1, 
    unit_id: null,
    unit_name: '' 
  })
}

// Remove recipe item
const removeRecipeItem = (idx) => {
  recipeItems.value.splice(idx, 1)
}

// On ingredient select
const onIngredientSelect = (idx, ingredientId) => {
  const ing = ingredients.value.find(i => i.id === ingredientId)
  if (ing) {
    recipeItems.value[idx].unit_id = ing.unit?.id ?? null  // ✅ SET unit_id
    recipeItems.value[idx].unit_name = ing.unit?.name ?? ''
  }
}

// Addendum 10, Modul AO — Tier Grosir (mirror pola Recipe di atas)
const openPriceTiers = async variant => {
  priceTierVariant.value = variant
  priceTierDialog.value = true
  priceTierLoading.value = true
  priceTierItems.value = []

  try {
    const response = await useApi(`/api/v1/variants/${variant.id}/price-tiers`).get()
    const data = response?.data ?? response

    priceTierItems.value = (data?.tiers ?? []).map(t => ({
      min_quantity: t.min_quantity,
      price_per_unit: t.price_per_unit,
    }))
  } catch (err) {
    console.error('Failed to load price tiers:', err)
    showSnackbar('Gagal memuat tier grosir', 'error')
  } finally {
    priceTierLoading.value = false
  }
}

const savePriceTiers = async () => {
  priceTierSaving.value = true
  try {
    const items = priceTierItems.value
      .filter(t => t.min_quantity && t.price_per_unit !== null && t.price_per_unit !== '')
      .map(t => ({ min_quantity: Number(t.min_quantity), price_per_unit: Number(t.price_per_unit) }))
      .sort((a, b) => a.min_quantity - b.min_quantity)

    await useApi(`/api/v1/variants/${priceTierVariant.value.id}/price-tiers`).put({
      body: { items },
    })

    showSnackbar('Tier grosir berhasil disimpan')
    priceTierDialog.value = false

    await fetchProduct()
  } catch (err) {
    console.error('Save price tiers error:', err)
    showSnackbar(err?.data?.message ?? 'Gagal menyimpan tier grosir', 'error')
  } finally {
    priceTierSaving.value = false
  }
}

const addPriceTierItem = () => {
  priceTierItems.value.push({ min_quantity: null, price_per_unit: null })
}

const removePriceTierItem = idx => {
  priceTierItems.value.splice(idx, 1)
}

// Helpers
const showSnackbar = (text, color = 'success') => {
  snackbar.value = { show: true, text, color }
}

const formatRupiah = value => {
  if (value == null) return '-'
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0,
  }).format(value)
}

onMounted(fetchProduct)

const breadcrumbs = [
  { title: 'Katalog', disabled: true },
  { title: 'Produk', to: { name: 'catalog-products' } },
  { title: 'Detail Produk', disabled: true },
]
</script>

<template>
  <div>
    <!-- Breadcrumbs -->
    <VBreadcrumbs :items="breadcrumbs" class="px-0 mb-4" />

    <!-- Loading -->
    <div v-if="loading" class="d-flex justify-center py-10">
      <VProgressCircular indeterminate color="primary" />
    </div>

    <template v-else-if="product">
      <!-- Header -->
      <VRow class="mb-4">
        <VCol cols="12">
          <div class="d-flex align-center justify-space-between flex-wrap gap-3">
            <div>
              <h4 class="text-h5 font-weight-bold">{{ product.name }}</h4>
              <p class="text-body-2 text-medium-emphasis mb-0">
                {{ product.description || 'Tidak ada deskripsi' }}
              </p>
            </div>
            <div class="d-flex gap-2">
              <VBtn
                v-if="ability.can('update', 'Product')"
                variant="tonal"
                :to="{ name: 'catalog-products-id-edit', params: { id: product.id } }"
              >
                Edit Produk
              </VBtn>
              <VBtn
                color="primary"
                :to="{ name: 'catalog-products' }"
              >
                Kembali
              </VBtn>
            </div>
          </div>
        </VCol>
      </VRow>

      <!-- Product Info -->
      <VRow>
        <VCol cols="12" md="4">
          <VCard>
            <VCardText class="text-center">
              <VAvatar
                v-if="product.image"
                :image="`/storage/${product.image}`"
                size="200"
                rounded="lg"
                class="mb-3"
              />
              <VAvatar
                v-else
                size="200"
                rounded="lg"
                color="primary"
                variant="tonal"
                class="mb-3"
              >
                <VIcon icon="ri-cup-line" size="80" />
              </VAvatar>
              <div>
                <VChip :color="product.is_active ? 'success' : 'default'" variant="tonal">
                  {{ product.is_active ? 'Aktif' : 'Non-aktif' }}
                </VChip>
              </div>
            </VCardText>
          </VCard>
        </VCol>

        <VCol cols="12" md="8">
          <!-- Variants -->
          <VCard>
            <VCardTitle class="pa-5 pb-0 d-flex align-center justify-space-between">
              <span>Varian & Recipe</span>
              <VChip color="info" variant="tonal">
                {{ product.variants?.length ?? 0 }} Varian
              </VChip>
            </VCardTitle>
            <VCardText class="pt-4">
              <div v-if="!product.variants?.length" class="text-center py-8">
                <VIcon icon="ri-list-unordered" size="48" color="medium-emphasis" class="mb-2" />
                <p class="text-body-2 text-medium-emphasis">Belum ada varian</p>
              </div>
              <VRow v-else>
                <VCol
                  v-for="variant in product.variants"
                  :key="variant.id"
                  cols="12"
                  sm="6"
                  md="6"
                >
                  <VCard variant="outlined" class="h-100">
                    <VCardText>
                      <div class="d-flex align-center justify-space-between mb-2">
                        <span class="font-weight-medium">{{ variant.name }}</span>
                        <VChip
                          :color="variant.is_active ? 'success' : 'default'"
                          size="x-small"
                          variant="tonal"
                        >
                          {{ variant.is_active ? 'Aktif' : 'Non-aktif' }}
                        </VChip>
                      </div>
                      <div class="text-body-2 text-medium-emphasis mb-1">
                        SKU: {{ variant.sku || '—' }}
                      </div>
                      <div class="text-body-2 text-medium-emphasis mb-1">
                        Barcode: {{ variant.barcode || '—' }}
                      </div>
                      <div class="text-h6 font-weight-bold text-primary mb-3">
                        {{ formatRupiah(variant.price) }}
                      </div>
                      <VBtn
                        block
                        variant="tonal"
                        size="small"
                        prepend-icon="ri-restaurant-line"
                        class="mb-2"
                        @click="openRecipe(variant)"
                      >
                        Kelola Recipe (BOM)
                      </VBtn>
                      <VBtn
                        block
                        variant="tonal"
                        size="small"
                        prepend-icon="ri-price-tag-3-line"
                        @click="openPriceTiers(variant)"
                      >
                        Tier Grosir
                      </VBtn>
                    </VCardText>
                  </VCard>
                </VCol>
              </VRow>
            </VCardText>
          </VCard>
        </VCol>
      </VRow>
    </template>

    <!-- Recipe Dialog -->
    <VDialog v-model="recipeDialog" max-width="700">
      <VCard>
        <VCardTitle class="pa-5 pb-0 d-flex align-center gap-2">
          Recipe — {{ selectedVariant?.name }}
          <VChip size="small" color="info" variant="tonal">BOM</VChip>
        </VCardTitle>
        <VCardText class="pt-4">
          <div v-if="recipeLoading" class="text-center py-6">
            <VProgressCircular indeterminate color="primary" />
          </div>
          <template v-else>
            <div v-if="!recipeItems.length" class="text-center py-6 mb-3">
              <VIcon icon="ri-restaurant-line" size="48" color="medium-emphasis" class="mb-2" />
              <p class="text-body-2 text-medium-emphasis">
                Belum ada bahan. Tambah untuk menghitung estimasi HPP.
              </p>
            </div>
            <div
              v-for="(item, idx) in recipeItems"
              :key="idx"
              class="mb-3"
            >
              <VRow dense align="center">
                <VCol cols="6">
                  <VAutocomplete
                    v-model="item.ingredient_id"
                    :items="ingredients"
                    item-title="name"
                    item-value="id"
                    label="Bahan *"
                    hide-details
                    @update:model-value="v => onIngredientSelect(idx, v)"
                  />
                </VCol>
                <VCol cols="4">
                  <VTextField
                    v-model.number="item.quantity"
                    label="Jumlah *"
                    type="number"
                    min="0"
                    step="0.01"
                    hide-details
                    :suffix="item.unit_name"
                  />
                </VCol>
                <VCol cols="2" class="text-right">
                  <VBtn
                    icon
                    variant="text"
                    size="small"
                    color="error"
                    @click="removeRecipeItem(idx)"
                  >
                    <VIcon icon="ri-delete-bin-line" />
                  </VBtn>
                </VCol>
              </VRow>
            </div>
            <VBtn
              variant="tonal"
              prepend-icon="ri-add-line"
              size="small"
              class="mt-2"
              @click="addRecipeItem"
            >
              Tambah Bahan
            </VBtn>
          </template>
        </VCardText>
        <VCardActions class="justify-end pa-5 pt-0 gap-3">
          <VBtn variant="tonal" @click="recipeDialog = false">Tutup</VBtn>
          <VBtn :loading="recipeSaving" @click="saveRecipe">Simpan Recipe</VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <!-- Tier Grosir Dialog (Addendum 10, Modul AO) -->
    <VDialog v-model="priceTierDialog" max-width="560">
      <VCard>
        <VCardTitle class="pa-5 pb-0 d-flex align-center gap-2">
          Tier Grosir — {{ priceTierVariant?.name }}
          <VChip size="small" color="info" variant="tonal">Retail</VChip>
        </VCardTitle>
        <VCardText class="pt-4">
          <div v-if="priceTierLoading" class="text-center py-6">
            <VProgressCircular indeterminate color="primary" />
          </div>
          <template v-else>
            <VAlert type="info" variant="tonal" density="compact" class="mb-4">
              Beli minimal sejumlah qty tertentu → harga per unit otomatis
              turun ke tier itu. Kosongkan semua tier untuk kembali ke harga
              varian biasa ({{ formatRupiah(priceTierVariant?.price) }}).
            </VAlert>

            <div v-if="!priceTierItems.length" class="text-center py-6 mb-3">
              <VIcon icon="ri-price-tag-3-line" size="48" color="medium-emphasis" class="mb-2" />
              <p class="text-body-2 text-medium-emphasis">
                Belum ada tier grosir. Harga tetap pakai harga varian biasa.
              </p>
            </div>

            <div
              v-for="(tier, idx) in priceTierItems"
              :key="idx"
              class="mb-3"
            >
              <VRow dense align="center">
                <VCol cols="5">
                  <VTextField
                    v-model.number="tier.min_quantity"
                    label="Minimal Qty *"
                    type="number"
                    min="1"
                    hide-details
                  />
                </VCol>
                <VCol cols="5">
                  <VTextField
                    v-model.number="tier.price_per_unit"
                    label="Harga per Unit *"
                    type="number"
                    min="0"
                    prefix="Rp "
                    hide-details
                  />
                </VCol>
                <VCol cols="2" class="text-right">
                  <VBtn
                    icon
                    variant="text"
                    size="small"
                    color="error"
                    @click="removePriceTierItem(idx)"
                  >
                    <VIcon icon="ri-delete-bin-line" />
                  </VBtn>
                </VCol>
              </VRow>
            </div>

            <VBtn
              variant="tonal"
              prepend-icon="ri-add-line"
              size="small"
              class="mt-2"
              @click="addPriceTierItem"
            >
              Tambah Tier
            </VBtn>
          </template>
        </VCardText>
        <VCardActions class="justify-end pa-5 pt-0 gap-3">
          <VBtn variant="tonal" @click="priceTierDialog = false">Tutup</VBtn>
          <VBtn :loading="priceTierSaving" @click="savePriceTiers">Simpan Tier Grosir</VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <VSnackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3000">
      {{ snackbar.text }}
    </VSnackbar>
  </div>
</template>
